const ENV = import.meta.env.MODE || 'development';

const DOMAIN = 'https://www.zh-h.cn';

export const API_PATHS = {
  local: 'http://127.0.0.1:8000/api/',
  production: `${DOMAIN}/api/`,
  development: `${DOMAIN}/dev/api/`,
  xuewen: `${DOMAIN}/xuewen/api/`,
} as const;

/** 与 API 预设对应的密文字体 CDN 根路径（不含末尾斜杠） */
export const CIPHER_FONT_CDN_PATHS = {
  production: `${DOMAIN}/static/fonts/cipher`,
  development: 'https://xuewen.zh-h.cn/static/fonts/cipher',
  xuewen: 'https://xuewen.zh-h.cn/static/fonts/cipher',
  local: 'http://127.0.0.1:8000/static/fonts/cipher',
  custom: 'http://127.0.0.1:8000/static/fonts/cipher',
} as const;

export type ApiPreset = keyof typeof API_PATHS;

const STORAGE_KEY_API_MODE = 'api_mode';
const STORAGE_KEY_CUSTOM_URL = 'api_custom_url';
const STORAGE_KEY_URL_HISTORY = 'api_url_history';
const MAX_HISTORY = 10;

export function getBaseUrl(): string {
  try {
    const mode = uni.getStorageSync(STORAGE_KEY_API_MODE);
    if (mode === 'custom') {
      const url = uni.getStorageSync(STORAGE_KEY_CUSTOM_URL);
      if (url) return url;
    }
    if (mode in API_PATHS) {
      return API_PATHS[mode as ApiPreset];
    }
  } catch (_) { }
  return API_PATHS[ENV === 'development' ? 'development' : 'production'];
}

export function setApiMode(mode: ApiPreset | 'custom', customUrl?: string): void {
  uni.setStorageSync(STORAGE_KEY_API_MODE, mode);
  if (mode === 'custom' && customUrl) {
    const url = customUrl.replace(/\/+$/, '') + '/';
    uni.setStorageSync(STORAGE_KEY_CUSTOM_URL, url);
    addUrlToHistory(url);
  }
}

export function getApiMode(): ApiPreset | 'custom' {
  try {
    const stored = uni.getStorageSync(STORAGE_KEY_API_MODE);
    if (stored === 'custom' || stored in API_PATHS) return stored;
  } catch (_) { }
  return 'production';
}

/** 从 API 根地址推导同机静态字库 CDN（如 http://127.0.0.1:8000/api/ → …/static/fonts/cipher） */
export function deriveFontCdnFromApiUrl(apiUrl: string): string {
  const normalized = apiUrl.trim();
  if (!normalized) return CIPHER_FONT_CDN_PATHS.local;
  const originMatch = normalized.match(/^(https?:\/\/[^/]+)/);
  if (originMatch) return `${originMatch[1]}/static/fonts/cipher`;
  return CIPHER_FONT_CDN_PATHS.local;
}

/** 本地 Django 静态字体（HTTP）；预设「本地」在小程序端不可用，自定义 API 联调时保留 */
const MP_LOCAL_HTTP_FONT_CDN_RE =
  /^http:\/\/(127\.0\.0\.1|localhost)(:\d+)?\/static\/fonts\/cipher\/?$/;

/** 小程序 loadFontFace 要求 HTTPS + CORS；仅「本地」预设回退，自定义 API 仍走本地字库 */
const MP_CIPHER_FONT_FALLBACK = CIPHER_FONT_CDN_PATHS.xuewen;

function isMpWeixinRuntime(): boolean {
  try {
    return typeof wx !== 'undefined' && typeof wx.loadFontFace === 'function';
  } catch {
    return false;
  }
}

function resolveCipherFontCdnForPlatform(
  base: string,
  mode: ApiPreset | 'custom',
): string {
  if (mode === 'custom') return base;
  if (isMpWeixinRuntime() && MP_LOCAL_HTTP_FONT_CDN_RE.test(base.replace(/\/$/, ''))) {
    if (import.meta.env.MODE === 'development') {
      console.warn(
        '[cipher-font][mp] local preset HTTP font CDN unavailable; using',
        MP_CIPHER_FONT_FALLBACK,
        '(use custom API for local font debugging)',
      );
    }
    return MP_CIPHER_FONT_FALLBACK;
  }
  return base;
}

/** 按当前 API 预设返回密文字体 CDN 根路径 */
export function getCipherFontCdnBase(): string {
  const mode = getApiMode();
  const base =
    mode === 'custom'
      ? deriveFontCdnFromApiUrl(getCustomUrl() || getBaseUrl())
      : (CIPHER_FONT_CDN_PATHS[mode] ?? CIPHER_FONT_CDN_PATHS.production);
  return resolveCipherFontCdnForPlatform(base, mode);
}

export function getCustomUrl(): string {
  try {
    return uni.getStorageSync(STORAGE_KEY_CUSTOM_URL) || '';
  } catch (_) { }
  return '';
}

export function getUrlHistory(): string[] {
  try {
    const raw = uni.getStorageSync(STORAGE_KEY_URL_HISTORY);
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw) return JSON.parse(raw);
  } catch (_) { }
  return [];
}

function addUrlToHistory(url: string): void {
  const history = getUrlHistory().filter(u => u !== url);
  history.unshift(url);
  if (history.length > MAX_HISTORY) history.length = MAX_HISTORY;
  uni.setStorageSync(STORAGE_KEY_URL_HISTORY, history);
}

export function removeUrlFromHistory(url: string): void {
  const history = getUrlHistory().filter(u => u !== url);
  uni.setStorageSync(STORAGE_KEY_URL_HISTORY, history);
}

export const BASE_URL = getBaseUrl();
export const DOMAIN_URL = DOMAIN;

export default {
  BASE_URL,
  getBaseUrl,
  setApiMode,
  getApiMode,
  getCipherFontCdnBase,
  deriveFontCdnFromApiUrl,
  getCustomUrl,
  getUrlHistory,
  removeUrlFromHistory,
  API_PATHS,
  CIPHER_FONT_CDN_PATHS,
  DOMAIN,
};
